/*!
  Pioneer WFRIA Tab v4.0.0-beta.53
  © 2023 Wells Fargo. All rights reserved.
*/
try {
  if (!window.__PIONEER_GLOBAL__) {
    window.__PIONEER_GLOBAL__ = {
      version: "4.0.0-beta.53",
      components: ["Tab"]
    };
  } else if (window.__PIONEER_GLOBAL__.components && window.__PIONEER_GLOBAL__.components.indexOf("Tab") < 0) {
    window.__PIONEER_GLOBAL__.components.push("Tab");
  }
} catch (error) {
  // catching server side rendering issues - windows is not defined there!
}

import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
const _excluded = ["children", "className", "disabled", "id", "lazyLoad", "title"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import { useTheme } from "@emotion/react";
import clsx from "clsx";
import PropTypes from "prop-types";
import React, { useContext, useRef } from "react";
import ReactDOM from "react-dom";
import { useUniqueId } from "../common/hooks";
import BaseButton from "../private/BaseButton/BaseButton";
import KeyboardNavigatorItem from "../private/KeyboardNavigator/KeyboardNavigatorItem";
import { TabsContext } from "../Tabs/TabsContext";
import extractTheme from "./extractTheme";
import { jsx as _jsx } from "@emotion/react/jsx-runtime";
import { jsxs as _jsxs } from "@emotion/react/jsx-runtime";
const SELECTED_ONLY = "selectedOnly";
/**
 * Tab is a React component that represents an individual view pane.
 * It can only be used as a child of Tabs.
 */

function Tab(_ref) {
  let {
    children,
    className,
    disabled,
    id,
    lazyLoad,
    title
  } = _ref,
      otherProps = _objectWithoutProperties(_ref, _excluded);

  const theme = useTheme();
  const {
    tabsPaneWrapperNode,
    onTabSelected,
    selected
  } = useContext(TabsContext);
  const {
    cssTabPaneContent,
    cssTabItemButton
  } = extractTheme(_objectSpread({
    theme,
    selected
  }, otherProps));
  const isActiveOnce = useRef(false);
  const tabUniqueId = useUniqueId({
    id
  });
  const paneId = `pane-${tabUniqueId}`;
  const itemId = `tab-button--${tabUniqueId}`;
  /**
   * @description Used to render the Tab children based on lazyLoad prop value
   * @param {node} tabPaneContent child of Tab component.
   * @param {boolean} selected holds the value of selected Tab.
   * @param {boolean} isActiveOnce used to know if we need to render all the Tab content.
   */

  const renderChild = tabPaneContent => {
    const shouldRenderTab = isActiveOnce.current || selected || !lazyLoad && typeof lazyLoad === "boolean";

    if (lazyLoad === SELECTED_ONLY) {
      if (selected) return tabPaneContent;
    } else if (shouldRenderTab) {
      isActiveOnce.current = true;
      return tabPaneContent;
    }
  };

  const tabPaneClassName = clsx({
    ["pnr-tab__pane-content--active"]: selected,
    ["wf-u-visually-hidden-content"]: !selected,
    [className]: className !== null
  });
  /**
   * Focus button explicitly because Safari does not focus buttons on
   * click. As a result, the arrow keys wouldn't work to navigate until
   * a Tab was focused in some other way. A timeout is used to prevent
   * `onSelectedTabChange` from being called twice.
   */

  const handleTabSelected = event => {
    const tabButton = event.currentTarget;
    event.persist();
    onTabSelected();
    setTimeout(() => tabButton === null || tabButton === void 0 ? void 0 : tabButton.focus(), 0);
  };

  const tabChild = /*#__PURE__*/ReactDOM.createPortal(_jsx("div", {
    className: tabPaneClassName,
    id: paneId,
    "aria-labelledby": itemId,
    role: "tabpanel",
    tabIndex: "0",
    "aria-hidden": !selected,
    css: cssTabPaneContent,
    children: renderChild(children)
  }), tabsPaneWrapperNode.current);
  return _jsxs(React.Fragment, {
    children: [_jsx(KeyboardNavigatorItem, {
      children: _jsx(BaseButton, {
        kind: "standard",
        id: itemId,
        role: "tab",
        disabled: disabled,
        "aria-disabled": disabled,
        "aria-selected": selected,
        "aria-controls": paneId,
        onClick: handleTabSelected,
        size: "large",
        buttonStyles: cssTabItemButton,
        noBox: true,
        children: title
      })
    }), tabChild]
  });
}

Tab.propTypes = process.env.NODE_ENV !== "production" ? {
  /** The body of the Tab. */
  children: PropTypes.node.isRequired,

  /** The title of the Tab. */
  title: PropTypes.string.isRequired,

  /** The CSS class name of the tab pane element. */
  className: PropTypes.string,

  /** Disable or enable the Tab. */
  disabled: PropTypes.bool,

  /**
   * id for the individual tab.
   */
  id: PropTypes.string,

  /**
   * Used to render Tab body content when selected.
   * <ul>
   *  <li>
   *    `false`: The Tab content is always rendered in the DOM.
   *    Switching Tabs hides the current Tab and shows the new Tab,
   *    without removing the Tab content from the DOM.
   *  </li>
   *  <li>
   *   `true`: The Tab content is rendered in the DOM only once the Tab is selected.
   *   Switching Tabs hides the current Tab and shows the new Tab,
   *   without removing the Tab content from the DOM.
   *  </li>
   *  <li>
   *   `selectedOnly`: Only the selected Tab is rendered in the DOM.
   *   Switching Tabs removes the current Tab from the DOM
   *   and renders only the content of the newly selected Tab.
   *  </li>
   * </ul>
   */
  lazyLoad: PropTypes.oneOfType([PropTypes.bool, PropTypes.oneOf([SELECTED_ONLY])])
} : {};
Tab.defaultProps = {
  children: null,
  className: null,
  disabled: false,
  id: null,
  lazyLoad: false,
  title: ""
};
export default Tab;